import React, { useMemo, useRef } from 'react';

import { useRouter } from 'next/router';
import { setSsrTokens } from 'utils/authentication';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { useQuery, useMutation, dehydrate, QueryClient } from 'react-query';
import { CareerPath } from 'services/core';
import UseToast from 'hooks/use-toast';
import { getErrorText } from 'utils/geterrortext';
import { GetCareerPathCertificate } from 'services/core';
import { downloadFile } from 'utils/fileConvert';
import { PROFILE_DETAIL_ERROR } from '@core/constants/Errors';

import { NextSeo } from 'next-seo';
import { getCareerPathDynamicProps } from 'router/seoprops';

import BackButton from '@core/components/backbutton/BackButton';
import CareerCourseCard from 'components/pages/career/careercoursecard/CareerCourseCard';
import ActionButton from '@core/components/actionbutton/ActionButton';
import Spinner from '@core/components/spinner/Spinner';
import RelatedCareerPathJob from 'components/pages/career/relatedcareerpathjobs/RelatedCareerPathJob';
import CareerPathReviews from 'components/pages/career/careerpathreviews/CareerPathReviews';
import LinkButton from '@core/components/linkbutton/LinkButton';
import FAQ from 'components/common/faq/FAQ';
import ContentExperts from 'components/common/contentexperts/ContentExperts';

import CareerPathCertificateImage from 'assets/images/pages/career-path/Career-Path-Certificate.png';
import OffIcon from '@core/assets/icons/50off.svg';
import { BookSquare, Cup } from 'iconsax-react';

import classes from './UserCareerPath.module.scss';
import AssessmentCard from 'components/pages/career/assessmentcard/AssessmentCard';
import CareerPathIntroVideo from 'components/pages/career/careerpathintrovideo/CareerPathIntroVideo';

const UserCareerPath = (props) => {
    const seoProps = getCareerPathDynamicProps({ ...props });
    const { ToastContainer, toast } = UseToast();
    const router = useRouter();
    const { data, isLoading, refetch } = useQuery(
        ['get-career-path', router?.query?.slug[0]],
        () => CareerPath.GET(router?.query?.slug[0]),
        {
            cacheTime: 0,
            staleTime: 0,
            enabled: !!router?.query?.slug[0]
        }
    );
    const { mutate } = useMutation(['enroll-career-path', router?.query?.slug[0]], () => CareerPath.POST(router?.query?.slug[0]), {
        onSuccess: (data) => {
            refetch();
            toast(data?.data?.message);
        },
        onError: (error) => {
            if (error.response.status === 401) {
                router.push('/login?next=/career-path/' + router?.query?.slug[0]);
            }
            else
                toast(getErrorText(error), 'alert');
        }
    });

    const { data: certificatePDF, isFetching, refetch: refetchCareerPathCertificate } = useQuery(['career-path-certificate'], () => GetCareerPathCertificate(router?.query?.slug[0]),
        {
            onSuccess: (certificatePDF) => {
                downloadFile(certificatePDF);
            },
            onError: (error) => {
                if (error?.response?.status === +PROFILE_DETAIL_ERROR) {
                    // showFullnameModal();
                    toast(
                        'Please fill in your full name to generate the certificate.',
                        'alert'
                    );
                }
                else
                    toast(
                        'There is a problem on the Certificare generation. Please try later.',
                        'alert'
                    );
            },
            cacheTime: 0,
            enabled: false,
        });

    const passedAssessmentsCount = data && data.data && Array.isArray(data.data.assessments)
        ? data.data.assessments.filter(a => a.last_status === 'passed').length
        : 0;

    /* Merge lectures and assessments by position */
    const mergedItems = useMemo(() => {
        const lectures = data?.data?.lectures || [];
        const assessments = data?.data?.assessments || [];
        // Clone lectures array
        const merged = [...lectures];
        // Insert each assessment at its position
        assessments.forEach((assessment) => {
            const pos = typeof assessment.position === 'number' ? assessment.position : merged.length;
            merged.splice(pos, 0, { ...assessment, __isAssessment: true });
        });
        return merged;
    }, [data]);

    // First card the user still has to finish: a lecture that is not completed
    // or an assessment that is not passed yet
    const nextStepIndex = useMemo(() => mergedItems.findIndex((item) => (
        item.__isAssessment
            ? item.last_status !== 'passed'
            : item.status !== 'completed'
    )), [mergedItems]);

    const nextStepRef = useRef(null);
    const coursesRef = useRef(null);

    const scrollToNextStep = () => {
        const target = nextStepRef.current || coursesRef.current;
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
        <>
            <NextSeo {...seoProps} />
            <ToastContainer />
            <div className='w-100'>
                <div className='container'>
                    <div className='row justify-content-center mx-1 mt-5'>
                        {/* <div className={classes['back-button']}>
                        <BackButton type='bordered' />
                        Career Paths
                    </div> */}
                        {(data && !isLoading) ? (
                            <>
                                <div
                                    className={classes['header']}
                                    style={{ backgroundImage: `linear-gradient(270deg, rgba(217, 217, 217, 0.00) -10%, #FFF 38.21%),url(${data.data.image})` }}
                                >
                                    <h2>{data.data.name}</h2>
                                    <div
                                        className={classes['description']}
                                        dangerouslySetInnerHTML={{ __html: data.data.description }}>
                                    </div>
                                    <div className={classes['info']}>
                                        {data.data.estimated_duration && (
                                            <div>
                                                <span>
                                                    Estimated path length
                                                </span>
                                                <h4>
                                                    {data.data.estimated_duration}
                                                </h4>
                                            </div>
                                        )}
                                        <div>
                                            <span>
                                                Courses
                                            </span>
                                            <div className={classes['icons-wrapper']}>
                                                {data.data.lectures.map((lecture, idx) => (
                                                    <span
                                                        key={idx}
                                                    >
                                                        {(lecture.status === 'completed') ? (
                                                            <BookSquare size={16} color="#F0AC0B" variant='Bold' />
                                                        ) : (
                                                            <BookSquare size={16} color="#CCCBC9" variant='Bold' />
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        {data.data.assessments.length > 0 && (
                                            <div>
                                                <span>
                                                    Assessments
                                                </span>
                                                <div className={classes['icons-wrapper']}>
                                                    {data.data.assessments.map((assessment, idx) => (
                                                        <span key={idx}>
                                                            <Cup
                                                                size={16}
                                                                color={idx < passedAssessmentsCount ? '#F0AC0B' : '#CCCBC9'}
                                                                variant='Bold'
                                                            />
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* <span>{data.data.lecture_count} Courses</span> */}
                                    {data.data.is_enrolled ? (
                                        data.data.is_completed ? (
                                            <ActionButton
                                                onClick={() => refetchCareerPathCertificate()}
                                                loading={isFetching}
                                            >
                                                Get Certificate
                                            </ActionButton>
                                        ) : (
                                            <ActionButton kind='secondary' onClick={scrollToNextStep}>
                                                Continue on Path
                                            </ActionButton>
                                        )
                                    ) : (
                                        <div className='d-flex gap-2 flex-md-row flex-column-reverse'>
                                            <ActionButton onClick={() => mutate()}>
                                                {data?.data?.can_enroll === false ? 'Subscribe to Start Path' : 'Enroll Path'}
                                            </ActionButton>
                                            {/* Blackfriday */}
                                            {/* <div className={classes['off-wrapper']}>
                                                <OffIcon />
                                                <div className='d-flex flex-column align-items-start justify-content-center'>
                                                    <h5>
                                                        Get this career path <span>50% off</span>
                                                    </h5>
                                                    <LinkButton kind='simple' href={'/plans'}>
                                                        Learn more <ArrowRight2 size={12} />
                                                    </LinkButton>
                                                </div>
                                            </div> */}
                                        </div>
                                    )}
                                </div>

                                {data.data.intro_video_url && (
                                    <CareerPathIntroVideo
                                        videoUrl={data.data.intro_video_url}
                                        thumbnail={data.data.intro_video_thumbnail}
                                        bulletHighlights={data.data.intro_bullet_highlights}
                                    />
                                )}

                                <div className='row mb-5 mt-4'>
                                    <div className='col-lg-9 col-12'>
                                        <div className={classes['courses']} ref={coursesRef}>
                                            <h4>Course list</h4>
                                            {mergedItems.map((item, idx) => {
                                                const wrapperRef = idx === nextStepIndex ? nextStepRef : undefined;
                                                if (item.__isAssessment) {
                                                    return <AssessmentCard key={`assessment-${item.id || idx}`} data={item} wrapperRef={wrapperRef} />;
                                                }
                                                return <CareerCourseCard key={`lecture-${item.id || idx}`} data={item} wrapperRef={wrapperRef} />;
                                            })}
                                        </div>
                                    </div>
                                    <div className='col-lg-3 col-12 '>
                                        <div className={classes['certificate-wrapper']}>
                                            <img src={CareerPathCertificateImage.src} alt='Career Path Certificate' />
                                            <h4>
                                                Pharmuni Certificate
                                            </h4>
                                            <p>
                                                Developed by Zamann Pharma Support, a TÜV ISO 9001-certified quality
                                                management service provider for European pharmaceutical companies,
                                            </p>
                                            <ActionButton onClick={() => refetchCareerPathCertificate()} disabled={!data.data.is_completed} loading={isFetching} >
                                                Get Certificate
                                            </ActionButton>
                                        </div>
                                    </div>
                                </div>

                            </>
                        ) : (
                            <div
                                className={`${classes['loading']} d-flex flex-row align-items-center justify-content-center`}>
                                <Spinner />
                            </div>
                        )}
                    </div>
                </div>

                {(data && !isLoading) && (data?.data?.experts?.length > 0) && (
                    <div className={classes['career-path-experts']}>
                        <div className='container px-1'>
                            <ContentExperts
                                experts={data?.data?.experts}
                                title='About the authors'
                            />
                        </div>
                    </div>
                )}
                {(data && !isLoading) && (
                    <div className='container px-lg-5 px-1 my-5'>
                        <CareerPathReviews
                            careerPathId={router?.query?.slug[0]}
                            rate={data?.data?.rate}
                        />
                    </div>
                )}
                {(data && !isLoading) && (data?.data?.jobs_count > 0) && (
                    <div className={classes['related-career-path-jobs']}>
                        <div className='container'>
                            <div className='d-flex align-items-center justify-content-between mb-3'>
                                <h3>{data?.data?.jobs_count} Relevant Jobs Available for {data?.data?.name} Career Path</h3>
                                <LinkButton kind='simple' href='/jobs' type='texted'>View All Jobs</LinkButton>
                            </div>

                            <RelatedCareerPathJob jobs={data?.data?.jobs} toast={toast} />
                        </div>
                    </div>
                )}
                {(data && !isLoading) && (data?.data?.faqs?.length > 0) && (
                    <div className='container my-5'>
                        <FAQ data={data?.data?.faqs} />
                    </div>
                )}

            </div>
        </>
    );
};



export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);
    const careerID = context?.params?.slug[0];
    try {
        const response = await CareerPath.GET(careerID);
        await queryClient.prefetchQuery(['get-career-path', careerID], () => ({ data: response.data }));
        let newQueryParams = '';
        const { slug, ...queryParams } = context.query;
        if (queryParams && Object.keys(queryParams).length > 0) {
            newQueryParams = `?${new URLSearchParams(queryParams).toString()}`;
        }
        if (!context?.params?.slug[1]) {
            context.res.setHeader(
                'Location',
                `/career-path/${context?.params?.slug[0]}/${response.data.slug}${newQueryParams}`
            );
            context.res.statusCode = 302;
            context.res.end();
        }
        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                id: careerID,
                title: response.data.name,
                description: response.data.meta_description,
                slug: response.data.slug,
                image: response.data.image
            }
        };
    } catch (e) {
        if (e.response.status === 404)
            return {
                redirect: {
                    permanent: false,
                    destination: "/404"
                }
            }
        else
            return {}
    }
}

export default UserCareerPath;